/**
 * API Client
 * Base fetch wrapper with authentication and error handling
 */

export const API_BASE_URL = import.meta.env.VITE_APP_SERVER_URL || 'http://localhost:8000';
export const AUTH_TOKEN = 'quran-token';
export const REFRESH_TOKEN = 'quran-refresh-token';
// Dispatched on `window` when the refresh token is missing or itself
// rejected, i.e. the user is genuinely logged out. Event-based instead of
// importing the auth store or router here, since both import from this
// module's callers (a static import would cycle back to this file).
export const AUTH_EXPIRED_EVENT = 'auth:expired';

interface FetchOptions extends RequestInit {
  requiresAuth?: boolean;
  /** Internal: set on the retried call so a still-401 response throws instead of refreshing again. */
  skipAuthRetry?: boolean;
}

// Shared by concurrent 401s so they await one refresh instead of each firing
// their own request against /api/user/refresh/.
let refreshPromise: Promise<string> | null = null;

async function refreshAccessToken(): Promise<string> {
  if (!refreshPromise) {
    refreshPromise = (async () => {
      const refreshToken = localStorage.getItem(REFRESH_TOKEN);
      if (!refreshToken) throw new Error('No refresh token stored');
      const { token } = await fetchAPI<{ token: string }>('/api/user/refresh/', {
        method: 'POST',
        body: JSON.stringify({ refresh_token: refreshToken }),
        requiresAuth: false,
      });
      localStorage.setItem(AUTH_TOKEN, token);
      return token;
    })().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

export class ApiError extends Error {
  status: number;
  data: Record<string, any>;

  constructor(message: string, status: number, data: Record<string, any> = {}) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
}

/**
 * Base fetch wrapper with authentication handling
 */
export async function fetchAPI<T = any>(
  endpoint: string,
  options: FetchOptions = {}
): Promise<T> {
  const { requiresAuth = true, skipAuthRetry = false, ...fetchOptions } = options;

  const headers: HeadersInit = {
    ...fetchOptions.headers,
  };

  // Only set application/json if not already set and body is not FormData
  if (!headers['Content-Type' as keyof HeadersInit] && !(fetchOptions.body instanceof FormData)) {
    (headers as any)['Content-Type'] = 'application/json';
  }

  // Add authentication token if required
  if (requiresAuth) {
    const token = localStorage.getItem(AUTH_TOKEN);
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
  }

  const url = endpoint.startsWith('http') ? endpoint : `${API_BASE_URL}${endpoint}`;

  const response = await fetch(url, {
    ...fetchOptions,
    headers,
  });

  if (!response.ok) {
    if (requiresAuth && response.status === 401 && !skipAuthRetry) {
      try {
        await refreshAccessToken();
        return fetchAPI<T>(endpoint, { ...options, skipAuthRetry: true });
      } catch {
        localStorage.removeItem(AUTH_TOKEN);
        localStorage.removeItem(REFRESH_TOKEN);
        window.dispatchEvent(new CustomEvent(AUTH_EXPIRED_EVENT));
      }
    }

    const errorData = await response.json().catch(() => ({}));
    const errorMessage =
      errorData.detail || errorData.message || errorData.error || response.statusText;
    throw new ApiError(errorMessage, response.status, errorData);
  }

  return response.json();
}
